import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SideDrawer from "../../components/SideDrawer";
import AskFilledNote from "../../components/assistant/AskFilledNote";
import Input from "../../components/Input";
import Button from "../../components/Button";
import ComingSoonButton from "../../components/ComingSoonButton";
import { FormGrid, FormGridItem } from "../../components/record";
import { extractErrorMessage, postReq } from "../../utils/request";
import { LeaveBalance, LeaveRequest, LeaveType, ParsedLeaveAnswer } from "../../types/Hr";
import classes from "./leave.module.scss";
import peopleClasses from "./people.module.scss";

interface LeaveForm {
    leaveTypeId: string;
    fromDate: string;
    toDate: string;
    halfDay: string;
    reason: string;
}

const EMPTY: LeaveForm = { leaveTypeId: "", fromDate: "", toDate: "", halfDay: "", reason: "" };

interface LeaveRequestDrawerProps {
    open: boolean;
    types: LeaveType[];
    balances: LeaveBalance[];
    /** Filing for someone else (HR); the employee's own request when absent. */
    employmentId?: number;
    employeeName?: string;
    onClose: () => void;
    onSaved: (request: LeaveRequest) => void;
    /** Values to start from (e.g. prepared by Ask); the person still checks and sends. */
    initial?: { leaveTypeId?: number | null; fromDate?: string | null; toDate?: string | null; reason?: string | null } | null;
    /** Fields Ask filled in (D9), shown at the top for the person to check. */
    askFilled?: string[];
}

/**
 * Ask for leave. Type it in plain words ("annual leave next Sunday to
 * Tuesday", "إجازة مرضية اليوم") to fill the form, or fill it directly.
 * Days are counted by the office's working days when the request is sent.
 */
const LeaveRequestDrawer: React.FC<LeaveRequestDrawerProps> = ({ open, types, balances, employmentId, employeeName, onClose, onSaved, initial, askFilled }) => {
    const [form, setForm] = useState<LeaveForm>(EMPTY);
    const [text, setText] = useState("");
    const [parsed, setParsed] = useState<ParsedLeaveAnswer | null>(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [wasParsed, setWasParsed] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            ...EMPTY,
            leaveTypeId: initial?.leaveTypeId ? String(initial.leaveTypeId) : types[0] ? String(types[0].leaveTypeId) : "",
            fromDate: initial?.fromDate ?? "",
            toDate: initial?.toDate ?? initial?.fromDate ?? "",
            reason: initial?.reason ?? "",
        });
        setText("");
        setParsed(null);
        setWasParsed(false);
    }, [open, types, initial]);

    const handleChange = (name: string, value: string | boolean | object) => {
        setForm((previous) => {
            const next = { ...previous, [name]: String(value) };
            if (name === "fromDate" && (!previous.toDate || previous.toDate < String(value))) next.toDate = String(value);
            if ((name === "fromDate" || name === "toDate") && next.fromDate !== next.toDate) next.halfDay = "";
            return next;
        });
    };

    const understand = async () => {
        if (!text.trim()) return;
        setIsParsing(true);
        try {
            const response = await postReq<ParsedLeaveAnswer>("hr/leave/parse", { text });
            if (response.status !== 200) {
                toast.error(extractErrorMessage(response.body, "Couldn't read that"));
                return;
            }
            const answer = response.body;
            setParsed(answer);
            setForm((previous) => ({
                ...previous,
                leaveTypeId: answer.leaveTypeId ? String(answer.leaveTypeId) : previous.leaveTypeId,
                fromDate: answer.fromDate ?? previous.fromDate,
                toDate: answer.toDate ?? previous.toDate,
                halfDay: answer.halfDay ?? "",
            }));
            setWasParsed(true);
        } finally {
            setIsParsing(false);
        }
    };

    const handleSave = async () => {
        if (isSaving) return;
        if (!form.leaveTypeId || !form.fromDate || !form.toDate) {
            toast.error("Pick the leave type and the dates");
            return;
        }
        setIsSaving(true);
        try {
            const response = await postReq<LeaveRequest>("hr/leave/requests", {
                employmentId,
                leaveTypeId: Number(form.leaveTypeId),
                fromDate: form.fromDate,
                toDate: form.toDate,
                halfDay: form.halfDay || null,
                reason: form.reason.trim() || null,
                source: wasParsed ? "parsed" : "web",
            });
            if (response.status === 200) {
                toast.success(`Leave asked for — ${response.body.days} day${response.body.days === 1 ? "" : "s"}, waiting on ${response.body.step === "manager" ? "the manager" : "HR"}`);
                onSaved(response.body);
            } else {
                toast.error(extractErrorMessage(response.body, "Couldn't send the request"));
            }
        } catch {
            toast.error("Couldn't send the request, please contact admin");
        } finally {
            setIsSaving(false);
        }
    };

    const type = types.find((item) => String(item.leaveTypeId) === form.leaveTypeId);
    const balance = balances.find((item) => String(item.leaveTypeId) === form.leaveTypeId);

    return (
        <SideDrawer open={open} title={employeeName ? `Leave for ${employeeName}` : "Ask for leave"} onClose={onClose} width={640}
            headerExtra={
                <Button type="button" variant="primary" icon="send" loading={isSaving} loadingText="Sending…" onClick={handleSave}
                    title="Send the request to the manager (or HR)">
                    Send
                </Button>
            }>
            <div className={peopleClasses.drawerForm}>
                {askFilled && <AskFilledNote fields={askFilled} />}
                <div className={classes.typeIt}>
                    <div className={classes.typeItRow}>
                        <div>
                            <Input type="text" label="Type it in your words" id="leave-text" value={text} onChange={(_, value) => setText(String(value))}
                                tooltip="English or Arabic — e.g. “annual leave next Sunday to Tuesday”, “sick tomorrow”, “إجازة سنوية من 12 إلى 14 أكتوبر”." />
                        </div>
                        <Button type="button" size="sm" icon="magic" loading={isParsing} onClick={understand} title="Fill the form from what you typed">
                            Fill the form
                        </Button>
                    </div>
                    {parsed && (
                        <span className={classes.understood}>
                            {parsed.understood.length > 0 ? `Understood: ${parsed.understood.join(" · ")}` : "Couldn't find a leave type or dates — fill them in below."}
                            {parsed.leaveTypeName && parsed.days !== null ? ` → ${parsed.leaveTypeName}, ${parsed.days} day${parsed.days === 1 ? "" : "s"}` : ""}
                            {parsed.availableAfter !== null ? `, ${parsed.availableAfter} left after` : ""}
                        </span>
                    )}
                    <ComingSoonButton size="xs" label="Understand anything (AI)" description="reads longer messages in any language — “back from my sister's wedding on the 20th, need the week before off” — and asks what's missing" />
                </div>

                <FormGrid>
                    <Input type="select" label="Leave type" id="leaveTypeId" value={form.leaveTypeId} onChange={handleChange} required
                        options={types.map((item) => ({ value: String(item.leaveTypeId), label: `${item.name}${item.isPaid ? "" : " (unpaid)"}` }))}
                        tooltip={type?.rule ?? "The kind of leave. Your employer's types and their rules."} />
                    <Input type="text" label="Left this year" id="leave-left" value={balance ? (balance.isUnlimited ? "No yearly limit" : `${balance.available} days`) : ""} onChange={() => undefined} disabled
                        tooltip="What's left of this type for the year, after requests still waiting." />
                    <Input type="date" label="From" id="fromDate" value={form.fromDate} onChange={handleChange} required tooltip="First day off." />
                    <Input type="date" label="To" id="toDate" value={form.toDate} onChange={handleChange} required tooltip="Last day off." />
                    <Input type="select" label="Half day" id="halfDay" value={form.halfDay} onChange={handleChange} disabled={!form.fromDate || form.fromDate !== form.toDate}
                        options={[{ value: "am", label: "Morning" }, { value: "pm", label: "Afternoon" }]}
                        tooltip="Only for a single day — counts as half a day." />
                    <FormGridItem wide>
                        <Input type="textarea" label="Reason" id="reason" value={form.reason} onChange={handleChange} rows={2}
                            tooltip={type?.documentAfterDays ? `Optional. Beyond ${type.documentAfterDays} days a supporting document (sick note) is needed — send it to HR.` : "Optional — what the approver should know."} />
                    </FormGridItem>
                </FormGrid>
                <p className={peopleClasses.subtle}>Weekends and your office's holidays aren't counted. Your shifts on those days are offered to colleagues once the leave is approved.</p>
            </div>
        </SideDrawer>
    );
};

export default LeaveRequestDrawer;
